// Animation templates — DETERMINISTIC motion recipes that the existing
// /animation route's WebCodecs encoder renders to MP4 (Jun 13). Counter-
// part to ai-templates.ts: same registry shape (built-ins + JSON overlay),
// but the "template" here is a (motion-style, motion-config, aspect,
// duration, slot-spec, preview MP4) tuple — no AI at runtime, no prompt,
// no sample image as scene authority.
//
// Click flow: /templates tile → /animation?template=<id> → page reads the
// template via useSearchParams + pre-populates the spec → user picks images
// from My Creatives → existing encoder produces the MP4.

import type { MotionStyle, AnimAspect, MotionConfig, TextStyle } from '@/lib/animation/types'
import type { AiTemplateDomain } from './ai-templates'
import customData from './animation-templates-custom.json'

export interface AnimationTemplate {
  id: string
  name: string
  /** Locked output aspect. A 9:16 mosaic ≠ a 1:1 mosaic — treat them as
   *  separate templates (matches the AI-template convention). */
  aspectRatio: AnimAspect
  /** Total clip duration in seconds. */
  durationSec: number
  /** Per-image hold in ms — drives the existing slideAt() math for the
   *  non-mosaic motion styles. Mosaic-scroll ignores this (its motion is
   *  driven by motionConfig). Default 2500. */
  perImageMs?: number
  motionStyle: MotionStyle
  motionConfig?: MotionConfig
  /** Optional caption defaults — set per template to lock the typography
   *  feel even though captions themselves are user-supplied (decision #1:
   *  template is motion-only, text editing comes later). */
  captionStyle?: TextStyle
  captionFont?: string
  captionWeight?: number
  /** Image-slot spec. `min` blocks export below this count; `recommended`
   *  is a soft hint shown to the user. With min=1 (the v1 default per
   *  decision #1) users can render with whatever they pick — looping is
   *  the caller's responsibility (the renderer wraps modulo content
   *  height). */
  imageSlots: { min: number; recommended: number }
  /** Canvas background color (hex). Only renders for motion styles that
   *  expose the canvas — mosaic-scroll (between gapped tiles), card-swipe
   *  (around the floating card). Defaults to '#EDEDED' light gray when
   *  unset. */
  backgroundColor?: string
  /** Domain tag → drives the "Recommended for <domain>" grouping on /templates.
   *  Same vocabulary as AI templates. Animation templates aren't strongly
   *  domain-coupled (they're motion, not scene), so most leave this unset. */
  domain?: AiTemplateDomain
  /** Path to a SHORT looping MP4 baked at author-time (decision #3 —
   *  generate by running the template through the encoder with the curated
   *  AI-template samples). Lives at frontend/public/animation-templates/
   *  previews/<id>.mp4. */
  previewVideo: string
}

// Built-in library. Custom templates authored via a future create-animation-
// template dev page (or hand-edited) land in animation-templates-custom.json
// and merge in below — same overlay pattern as ai-templates-custom.json.
const BUILTIN_ANIMATION_TEMPLATES: readonly AnimationTemplate[] = [
  {
    id: 'mosaic-scroll-portrait',
    name: 'Mosaic Scroll',
    // Jun 13 — switched 9:16 → 4:5 (Instagram feed portrait standard, 1080×1350).
    // The center-focus mosaic layout reads well at this aspect; the 9:16 vertical
    // version is a separate template if/when we author it.
    aspectRatio: '4:5',
    durationSec: 12,
    motionStyle: 'mosaic-scroll',
    motionConfig: {
      kind: 'mosaic-scroll',
      columns: 3,
      scrollMs: 1200,
      holdMs: 800,
      columnStaggerMs: 200,
    },
    imageSlots: { min: 1, recommended: 12 },
    previewVideo: '/animation-templates/previews/mosaic-scroll-portrait.mp4',
  },
  {
    id: 'angled-cut-portrait',
    name: 'Angled Cut',
    // 4:5 portrait — image split by a single near-horizontal line tilted 12°
    // from horizontal (Jun 13 — was vertical, switched to horizontal). Top
    // half slides in from off-canvas top, bottom half from off-canvas
    // bottom; phase offset (350ms) makes top arrive first, then bottom. At
    // rest the two halves align contiguously — no padding between, image
    // looks intact. Dark bg for slide-in drama.
    // Cycle math (Jun 13 — seamless loop fix): scrollMs + holdMs = 2800ms.
    // 14s = 5 × 2.8s ✓
    aspectRatio: '4:5',
    durationSec: 14,
    motionStyle: 'angled-cut',
    motionConfig: {
      kind: 'angled-cut',
      orientation: 'horizontal',
      tiltDeg: 12,
      scrollMs: 1100,
      holdMs: 1700,
      regionPhaseMs: 350,
    },
    backgroundColor: '#1A1A1A',
    imageSlots: { min: 1, recommended: 3 },
    previewVideo: '/animation-templates/previews/angled-cut-portrait.mp4',
  },
  {
    id: 'collage-stack-portrait',
    name: 'Collage Stack',
    // 4:5 portrait — images stop-motion onto a stack, each landing with a
    // small rotation + paper-photo border on cream bg. Scrapbook feel.
    // 700ms tick = 6 images stacked over ~4.2s, then 1.8s hold before the
    // cycle resets. recommended:6 matches the typical scrapbook density.
    aspectRatio: '4:5',
    durationSec: 12,
    motionStyle: 'collage-stack',
    motionConfig: {
      kind: 'collage-stack',
      tickMs: 700,
      holdMs: 1800,
      maxRotationDeg: 8,
      cardWidthFrac: 0.65,
      cardWidthJitter: 0.1,
    },
    backgroundColor: '#F5F1EA',
    imageSlots: { min: 1, recommended: 6 },
    previewVideo: '/animation-templates/previews/collage-stack-portrait.mp4',
  },
  {
    id: 'mood-board-portrait',
    name: 'Mood Board',
    // 4:5 portrait — 3×2 grid of square tiles. Each tile has its OWN tick
    // rate (600-1800ms) and flips between showing a random image from the
    // input set and being blank (80% show). Deterministic hash-based
    // randomness so the live preview + encoded MP4 stay byte-identical.
    // Cream bg matches the mood-board reference's neutral surface.
    aspectRatio: '4:5',
    durationSec: 12,
    motionStyle: 'stop-motion-grid',
    motionConfig: {
      kind: 'stop-motion-grid',
      cols: 3,
      rows: 2,
      tickMinMs: 600,
      tickMaxMs: 1800,
      showProbability: 0.8,
      tileAspectRatio: 1,
    },
    backgroundColor: '#F5F1EA',
    imageSlots: { min: 1, recommended: 6 },
    previewVideo: '/animation-templates/previews/mood-board-portrait.mp4',
  },
  {
    id: 'split-reveal-portrait',
    name: 'Split Reveal',
    // 4:5 portrait — same scroll math as Marquee Rows, but each row renders
    // ONE third of the image (top/middle/bottom). During the long hold the
    // strips align horizontally → the image assembles. Phase offset (500ms)
    // creates a cascade reveal: top strip lands first, then middle, then
    // bottom. holdMs (1600) comfortably exceeds 2*phase so all 3 rows
    // overlap in hold = the assembled image gets a moment to breathe.
    aspectRatio: '4:5',
    durationSec: 14,
    motionStyle: 'marquee-rows',
    motionConfig: {
      kind: 'marquee-rows',
      scrollMs: 1200,
      holdMs: 1600,
      rowPhaseMs: 500,
      tileWidthFrac: 1,
      splitImage: true,
    },
    backgroundColor: '#1A1A1A',
    imageSlots: { min: 1, recommended: 1 },
    previewVideo: '/animation-templates/previews/split-reveal-portrait.mp4',
  },
  {
    id: 'marquee-rows-portrait',
    name: 'Marquee Rows',
    // 4:5 portrait — 3 horizontal rows of the same image sliding right-to-
    // left, phase-offset for visual rhythm. Dark bg ships with the template
    // for the cinematic VW Tiguan-style feel from the reference.
    // Cycle math (Jun 13 — seamless loop fix): scrollMs + holdMs = 2000ms.
    // durationSec * 1000 must be a whole multiple of cycle ms or the MP4
    // loops back to t=0 from mid-cycle and stutters. 12s = 6 × 2s ✓
    aspectRatio: '4:5',
    durationSec: 12,
    motionStyle: 'marquee-rows',
    motionConfig: {
      kind: 'marquee-rows',
      scrollMs: 1500,
      holdMs: 500,
      rowPhaseMs: 250,
      tileWidthFrac: 1,
    },
    backgroundColor: '#1A1A1A',
    imageSlots: { min: 1, recommended: 1 },
    previewVideo: '/animation-templates/previews/marquee-rows-portrait.mp4',
  },
  {
    id: 'card-swipe-portrait',
    name: 'Card Swipe',
    // 4:5 portrait — single hero card on gray bg, previous + next peek the
    // edges, eases right-to-left then dwells on each card. Matches the
    // "Bake House Yummy" reference (Jun 13).
    // Cycle math (Jun 13 — seamless loop fix): transitionMs + holdMs = 2500ms.
    // 15s = 6 × 2.5s ✓
    aspectRatio: '4:5',
    durationSec: 15,
    motionStyle: 'card-swipe',
    motionConfig: {
      kind: 'card-swipe',
      transitionMs: 800,
      holdMs: 1700,
      cardWidthFrac: 0.55,
      cardHeightFrac: 0.75,
    },
    imageSlots: { min: 1, recommended: 5 },
    previewVideo: '/animation-templates/previews/card-swipe-portrait.mp4',
  },
]

const CUSTOM_ANIMATION_TEMPLATES: AnimationTemplate[] = (customData as AnimationTemplate[])
  .filter((t) => t && typeof t.id === 'string' && typeof t.name === 'string')

export const ANIMATION_TEMPLATES: readonly AnimationTemplate[] = [
  ...BUILTIN_ANIMATION_TEMPLATES,
  ...CUSTOM_ANIMATION_TEMPLATES,
]

export function getAnimationTemplate(id: string): AnimationTemplate | undefined {
  return ANIMATION_TEMPLATES.find((t) => t.id === id)
}
